import React, { useState, useMemo } from 'react'
import { useStore, usePerformanceConfig } from '../../state/store'
import CollapsibleSection from './CollapsibleSection'

const BasicParamsPanel: React.FC = () => {
  const { settings, updateSettings } = useStore()

  return (
    <div className="space-y-2">
      <div className="slider-container">
        <span className="slider-label">字体大小</span>
        <input
          type="range"
          min="6"
          max="20"
          step="1"
          value={settings.fontSize}
          onChange={(e) => updateSettings({ fontSize: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="slider-value">{settings.fontSize}</span>
      </div>

      <div className="slider-container">
        <span className="slider-label">字符间距</span>
        <input
          type="range"
          min="0.7"
          max="2"
          step="0.05"
          value={settings.charSpacing}
          onChange={(e) => updateSettings({ charSpacing: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="slider-value">{settings.charSpacing.toFixed(2)}</span>
      </div>

      <div className="slider-container">
        <span className="slider-label">对比度</span>
        <input
          type="range"
          min="0"
          max="2"
          step="0.05"
          value={settings.contrast}
          onChange={(e) => updateSettings({ contrast: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="slider-value">{settings.contrast.toFixed(2)}</span>
      </div>

      <div className="slider-container">
        <span className="slider-label">亮度</span>
        <input
          type="range"
          min="-1"
          max="1"
          step="0.05"
          value={settings.brightness}
          onChange={(e) => updateSettings({ brightness: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="slider-value">{settings.brightness.toFixed(2)}</span>
      </div>

      <div className="slider-container">
        <span className="slider-label">透明度</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={settings.opacity}
          onChange={(e) => updateSettings({ opacity: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="slider-value">{settings.opacity.toFixed(2)}</span>
      </div>

      <div className="xp-checkbox-row">
        <span className="xp-field-label">反色</span>
        <input
          type="checkbox"
          checked={settings.invert}
          onChange={(e) => updateSettings({ invert: e.target.checked })}
        />
      </div>
    </div>
  )
}

const ColorParamsPanel: React.FC = () => {
  const { settings, updateSettings } = useStore()

  return (
    <div className="space-y-2">
      <div className="xp-checkbox-row">
        <span className="xp-field-label">颜色模式</span>
        <select
          value={settings.colorMode}
          onChange={(e) => updateSettings({ colorMode: e.target.value as any })}
          className="w-40"
        >
          <option value="grayscale">灰度</option>
          <option value="color">彩色</option>
          <option value="green">绿色终端</option>
          <option value="amber">琥珀终端</option>
          <option value="custom">自定义</option>
        </select>
      </div>

      <div className="xp-checkbox-row">
        <span className="xp-field-label">背景颜色</span>
        <input
          type="color"
          value={settings.backgroundColor}
          onChange={(e) => updateSettings({ backgroundColor: e.target.value })}
          className="w-14"
        />
      </div>

      {settings.colorMode === 'custom' && (
        <div className="xp-checkbox-row">
          <span className="xp-field-label">前景颜色</span>
          <input
            type="color"
            value={settings.customColor}
            onChange={(e) => updateSettings({ customColor: e.target.value })}
            className="w-14"
          />
        </div>
      )}
    </div>
  )
}

const CharsetParamsPanel: React.FC = () => {
  const { settings, updateSettings } = useStore()

  return (
    <div className="space-y-2">
      <div className="xp-checkbox-row">
        <span className="xp-field-label">字符集</span>
        <select
          value={settings.charset}
          onChange={(e) => updateSettings({ charset: e.target.value as any })}
          className="w-40"
        >
          <option value="standard">标准</option>
          <option value="blocks">方块</option>
          <option value="detailed">详细</option>
          <option value="minimal">极简</option>
          <option value="binary">二进制</option>
          <option value="letters-alphabet">字母 (A-Z)</option>
          <option value="letters-lowercase">字母 (a-z)</option>
          <option value="letters-mixed">字母 (混合)</option>
          <option value="letters-symbols">符号</option>
          <option value="custom">自定义</option>
        </select>
      </div>

      {settings.charset === 'custom' && (
        <input
          type="text"
          value={settings.customCharset}
          onChange={(e) => updateSettings({ customCharset: e.target.value })}
          placeholder="输入自定义字符集"
          className="xp-input"
        />
      )}
    </div>
  )
}

const OutputParamsPanel: React.FC = () => {
  const { settings, updateSettings } = useStore()

  return (
    <div className="space-y-2">
      <div className="xp-checkbox-row">
        <span className="xp-field-label">纵横比</span>
        <select
          value={settings.outputAspect}
          onChange={(e) => updateSettings({ outputAspect: e.target.value as any })}
          className="w-40"
        >
          <option value="source">原始比例</option>
          <option value="16:9">16:9</option>
          <option value="4:3">4:3</option>
          <option value="1:1">1:1</option>
          <option value="3:4">3:4</option>
          <option value="9:16">9:16</option>
        </select>
      </div>

      <div className="xp-checkbox-row">
        <span className="xp-field-label">渲染字体</span>
        <select
          value={settings.renderFont}
          onChange={(e) => updateSettings({ renderFont: e.target.value })}
          className="w-40"
        >
          <option value='"Helvetica Neue", Helvetica, Arial, sans-serif'>Helvetica</option>
          <option value='"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'>Inter</option>
          <option value='"Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif'>Poppins</option>
          <option value='"Space Grotesk", "Helvetica Neue", Helvetica, Arial, sans-serif'>Space Grotesk</option>
          <option value='"VT323", "Arial", sans-serif'>VT323</option>
        </select>
      </div>
    </div>
  )
}

const PerformanceParamsPanel: React.FC = () => {
  const performanceConfig = usePerformanceConfig()
  const { updatePerformanceConfig } = useStore()

  return (
    <div className="space-y-2">
      <div className="xp-note">
        性能优化会在字符数量过高时自动缩放输入图片，优先保证预览流畅。
      </div>

      <div className="slider-container">
        <span className="slider-label">字符上限</span>
        <input
          type="range"
          min="5000"
          max="20000"
          step="1000"
          value={performanceConfig.maxCharsLimit}
          onChange={(e) => updatePerformanceConfig({ maxCharsLimit: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="slider-value">{performanceConfig.maxCharsLimit.toLocaleString()}</span>
      </div>

      <div className="xp-muted text-[15px] font-semibold">
        建议保持在 10,000 左右，以获得较稳定的实时预览性能。
      </div>

      <div className="xp-checkbox-row">
        <div>
          <div className="xp-field-label">保持原始尺寸</div>
          <div className="xp-muted text-[15px] font-semibold">开启后可能导致大型图片渲染变慢</div>
        </div>
        <input
          type="checkbox"
          checked={performanceConfig.preserveOriginalSize}
          onChange={(e) => updatePerformanceConfig({ preserveOriginalSize: e.target.checked })}
        />
      </div>

      {performanceConfig.preserveOriginalSize && (
        <div className="xp-warning">
          已启用原始尺寸模式，超大图片可能出现明显卡顿。
        </div>
      )}
    </div>
  )
}

const SettingsPanel: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const { resetAll } = useStore()

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return null

    const query = searchQuery.toLowerCase()
    const results: string[] = []
    const searchableItems = [
      { name: '字体大小', section: '基础参数' },
      { name: '字符间距', section: '基础参数' },
      { name: '对比度', section: '基础参数' },
      { name: '亮度', section: '基础参数' },
      { name: '透明度', section: '基础参数' },
      { name: '反色', section: '基础参数' },
      { name: '颜色模式', section: '颜色' },
      { name: '背景颜色', section: '颜色' },
      { name: '前景颜色', section: '颜色' },
      { name: '字符集', section: '字符集' },
      { name: '自定义字符集', section: '字符集' },
      { name: '纵横比', section: '输出' },
      { name: '渲染字体', section: '输出' },
    ]

    searchableItems.forEach((item) => {
      if (item.name.toLowerCase().includes(query)) {
        results.push(item.section)
      }
    })

    return [...new Set(results)]
  }, [searchQuery])

  return (
    <div className="panel-section">
      <div className="xp-section-header">
        <h3 className="panel-title">常规设置</h3>
      </div>
      <div className="xp-section-body space-y-3">
        <div className="space-y-1">
          <label className="xp-group-label" htmlFor="settings-search">
            参数搜索
          </label>
          <div className="flex gap-1">
            <input
              id="settings-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索参数..."
              className="xp-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="xp-btn xp-btn-small"
              >
                清除
              </button>
            )}
          </div>
          {filteredSections && filteredSections.length > 0 && (
            <div className="xp-note">
              匹配分组: {filteredSections.join(' / ')}
            </div>
          )}
        </div>

        <CollapsibleSection title="基础参数" icon="BAS" defaultExpanded={true}>
          <BasicParamsPanel />
        </CollapsibleSection>

        <CollapsibleSection title="颜色" icon="CLR" defaultExpanded={false}>
          <ColorParamsPanel />
        </CollapsibleSection>

        <CollapsibleSection title="字符集" icon="CHR" defaultExpanded={false}>
          <CharsetParamsPanel />
        </CollapsibleSection>

        <CollapsibleSection title="输出" icon="OUT" defaultExpanded={false}>
          <OutputParamsPanel />
        </CollapsibleSection>

        <CollapsibleSection title="性能" icon="PER" defaultExpanded={true}>
          <PerformanceParamsPanel />
        </CollapsibleSection>

        <button
          onClick={resetAll}
          className="btn-secondary w-full"
        >
          重置全部参数
        </button>
      </div>
    </div>
  )
}

export default SettingsPanel
